import { cite, type Cite, type CiteKey, type JobRow, type Msg } from './types';
import { parseSse } from './sse';

/** Gửi POST `/ai/v1/ask`; Hub bọc token Office 365 và địa chỉ worker (apps/hub lắp). */
export type AskTransport = (body: { messages: Array<{ role: 'user' | 'assistant'; text: string }> }, signal: AbortSignal) => Promise<Response>;

export type AskError = 'noperm' | 'disabled' | 'busy' | 'network' | 'unknown';

export class AskFailure extends Error {
  constructor(readonly code: AskError) { super(code); }
}

export type AskEvent =
  | { type: 'text'; delta: string }
  | { type: 'tool'; name: string; status: string }
  | { type: 'card'; tool: string; card?: { kind: string; data: unknown }; cite?: string[] }
  | { type: 'done'; end: string; traceId?: string };

const str = (v: unknown) => (typeof v === 'string' && v ? v : '');

export function statusToError(status: number): AskError {
  if (status === 401 || status === 403) return 'noperm';
  if (status === 503) return 'disabled';
  if (status === 429) return 'busy';
  return status >= 500 ? 'network' : 'unknown';
}

/** Gọi worker và trả từng sự kiện. Ném `AskFailure` khi HTTP lỗi hoặc mất kết nối; huỷ bằng `signal`. */
export async function* streamAsk(send: AskTransport, messages: Array<{ role: 'user' | 'assistant'; text: string }>, signal: AbortSignal): AsyncGenerator<AskEvent> {
  let res: Response;
  try {
    res = await send({ messages }, signal);
  } catch (e) {
    if (signal.aborted) return;
    throw new AskFailure(e instanceof AskFailure ? e.code : 'network');
  }
  if (!res.ok || !res.body) throw new AskFailure(statusToError(res.status));
  try {
    for await (const ev of parseSse(res.body)) {
      const d = (ev.data ?? {}) as Record<string, unknown>;
      if (ev.event === 'text') yield { type: 'text', delta: str(d.delta) };
      else if (ev.event === 'tool') yield { type: 'tool', name: str(d.name), status: str(d.status) };
      else if (ev.event === 'card') yield { type: 'card', tool: str(d.tool), card: d.card as { kind: string; data: unknown } | undefined, cite: Array.isArray(d.cite) ? d.cite.filter((x): x is string => typeof x === 'string') : undefined };
      else if (ev.event === 'done') yield { type: 'done', end: str(d.end), traceId: str(d.traceId) || undefined };
    }
  } catch {
    if (!signal.aborted) throw new AskFailure('network');
  }
}

const CITE_BY_PATH: Array<[string, CiteKey]> = [
  ['/ezy/jobs', 'job'], ['/ezy/sop', 'sop'], ['/ezy/schedule', 'sched'], ['/ezy/booking', 'booking'], ['/ezy/bl', 'bl'],
  ['/ezy/tasks', 'tasks'], ['/ezy/tms', 'tms'], ['/ezy/customers', 'customers'], ['/box', 'box'],
];

/** Đường dẫn nguồn của công cụ → thẻ nguồn trên màn; đường dẫn lạ bị bỏ (không hiện link không có trong sổ). */
export function citesFromPaths(paths: string[] | undefined): Cite[] {
  const keys: CiteKey[] = [];
  for (const p of paths ?? []) {
    const hit = CITE_BY_PATH.find(([prefix]) => p === prefix || p.startsWith(`${prefix}/`));
    if (hit && !keys.includes(hit[1])) keys.push(hit[1]);
  }
  return cite(...keys);
}

const HOUR = 3_600_000;

function leftLabel(ms: number): string {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))} phút`;
  if (ms < 24 * HOUR) return `${Math.round(ms / HOUR)} giờ`;
  return `${Math.round(ms / (24 * HOUR))} ngày`;
}

/** Dòng `jobs_cutoff` → dòng thẻ Job (hạn gần nhất của SI / CY). */
export function jobRowsFromCard(data: unknown, now: number): JobRow[] {
  if (!Array.isArray(data)) return [];
  const rows: JobRow[] = [];
  for (const raw of data) {
    const r = (raw ?? {}) as Record<string, unknown>;
    const id = str(r.jobId);
    if (!id) continue;
    const times = [str(r.closeDoc), str(r.closeCY)].map((s) => Date.parse(s)).filter((x) => Number.isFinite(x) && x >= now);
    const ms = times.length ? Math.min(...times) - now : 0;
    const route = [str(r.customer), str(r.booking)].filter(Boolean).join(' · ');
    rows.push({
      id, route: route || id, sub: [str(r.vessel), str(r.etd)].filter(Boolean).join(' · ') || id,
      left: leftLabel(ms), pct: Math.min(100, Math.round((ms / (24 * HOUR)) * 100)), tone: ms <= 4 * HOUR ? 'err' : 'warn', st: str(r.status) || '—',
    });
  }
  return rows;
}

/** Dựng thẻ trả lời từ chữ + dữ liệu công cụ (thẻ và nguồn lấy từ dữ liệu, không từ chữ mô hình). */
export function buildReply(text: string, cards: Array<Extract<AskEvent, { type: 'card' }>>, now: number): Msg {
  const cites = citesFromPaths(cards.flatMap((c) => c.cite ?? []));
  const jobs = cards.find((c) => c.card?.kind === 'jobs');
  const rows = jobs ? jobRowsFromCard(jobs.card?.data, now) : [];
  if (rows.length) return { ai: true, kind: 'jobs', text, rows, acts: [], cites };
  return { ai: true, kind: 'plain', text, cites };
}
